import classNames from "classnames";
import {ReactElement} from "react";
import {Link, LinkProps} from "react-router-dom";

import styles from "./Button.module.css";

const variants = {
  filled: styles.filled,
  unfilled: styles.unfilled,
  minimal: styles.minimal,
} as const;

const variantsLoading = {
  filled: styles.filledLoading,
  unfilled: styles.unfilledLoading,
  minimal: styles.minimalLoading,
} as const;

const loaders = {
  filled: styles.loaderFilled,
  unfilled: styles.loaderUnfilled,
  minimal: styles.loaderMinimal,
} as const;

export type ButtonLinkProps =
  | {
      to: string;
      state?: LinkProps["state"];
      replace?: LinkProps["replace"];
      onClick?: React.MouseEventHandler<HTMLAnchorElement>;
      href?: never;
      target?: never;
      rel?: never;
    }
  | {
      to?: never;
      onClick?: React.MouseEventHandler<HTMLButtonElement>;
      href?: never;
      target?: never;
      rel?: never;
    }
  | {
      to?: never;
      onClick?: never;
      href: string;
      target?: "_blank";
      rel?: "noopener noreferrer";
    };

type Props = {
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  type?: "button" | "submit";
  colorVariant?: "brand" | "grey";
  variant?: "filled" | "unfilled" | "minimal";
  iconLeft?: (color: string) => ReactElement;
  iconRight?: (color: string) => ReactElement;
  children: string;
} & ButtonLinkProps;

export default function Button(prop: Props) {
  const variant = prop.variant ?? "filled";
  const colorVariant = prop.colorVariant ?? "brand";

  function renderContent() {
    return (
      <span
        className={classNames(
          prop.loading
            ? [styles.loader, loaders[variant]]
            : [styles.label, prop.labelClassName],
        )}
      >
        {prop.loading ? null : prop.children}
      </span>
    );
  }

  if ("href" in prop) {
    return (
      <a
        className={classNames(
          prop.loading ? variantsLoading[variant] : variants[variant],
          styles.link,
          prop.className,
          colorVariant === "grey" && styles.grey,
          styles.button,
        )}
        href={prop.loading ? undefined : prop.href}
        target={prop.target}
        rel={prop.rel}
      >
        <span className={styles.icon}>
          {prop.iconLeft === undefined || prop.loading
            ? null
            : prop.disabled
            ? prop.iconLeft("#9f9f9f")
            : prop.iconLeft("var(--brand-primary-color)")}
        </span>
        {renderContent()}
        <span className={styles.icon}>
          {prop.iconRight === undefined || prop.loading
            ? null
            : prop.disabled
            ? prop.iconRight("#9f9f9f")
            : prop.iconRight("var(--brand-primary-color)")}
        </span>
        <div className={prop.disabled ? undefined : styles.activeBackground} />
      </a>
    );
  } else if (!("to" in prop)) {
    return (
      <button
        onClick={prop.onClick}
        disabled={prop.disabled || prop.loading}
        className={classNames(
          prop.loading ? variantsLoading[variant] : variants[variant],
          prop.className,
          colorVariant === "grey" && styles.grey,
          styles.button,
        )}
        type={prop.type}
      >
        <span className={styles.icon}>
          {prop.iconLeft === undefined || prop.loading
            ? null
            : prop.disabled
            ? prop.iconLeft("#9f9f9f")
            : prop.iconLeft("var(--brand-primary-color)")}
        </span>
        {renderContent()}
        <span className={styles.icon}>
          {prop.iconRight === undefined || prop.loading
            ? null
            : prop.disabled
            ? prop.iconRight("#9f9f9f")
            : prop.iconRight("var(--brand-primary-color)")}
        </span>
        <div className={prop.disabled ? undefined : styles.activeBackground} />
      </button>
    );
  } else if (prop.disabled) {
    return (
      <button
        disabled={true}
        className={classNames(
          prop.className,
          styles.button,
          variants[variant],
          colorVariant === "grey" && styles.grey,
        )}
        type={prop.type}
      >
        <span className={styles.icon}>
          {prop.iconLeft === undefined || prop.loading
            ? null
            : prop.iconLeft("#9f9f9f")}
        </span>
        {renderContent()}
        <span className={styles.icon}>
          {prop.iconRight === undefined || prop.loading
            ? null
            : prop.iconRight("#9f9f9f")}
        </span>
      </button>
    );
  } else if (prop.to) {
    return (
      <Link
        className={
          location.pathname !== undefined
            ? classNames(
                variants[variant],
                styles.button,
                styles.link,
                colorVariant === "grey" && styles.grey,
                prop.className,
              )
            : undefined
        }
        to={prop.to}
        state={prop.state}
        replace={prop.replace}
        onClick={prop.onClick}
      >
        <span className={styles.icon}>
          {prop.iconLeft === undefined || prop.loading
            ? null
            : prop.disabled
            ? prop.iconLeft("#9f9f9f")
            : prop.iconLeft("#b00002")}
        </span>
        {renderContent()}
        <div className={prop.disabled ? undefined : styles.activeBackground} />
      </Link>
    );
  } else {
    return null;
  }
}
